import {
  GetDashboardByTokenQuery,
  useGetDashboardByTokenQuery,
} from 'generated/graphql';
import { useMemo } from 'react';
import { Dashboard } from 'v2/core/entities/dashboard';
import mapFromEdgeDashboard from 'v2/core/repository/edge-server/dashboard/fromEdgeDashboard';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import {
  EdgeResponse,
  GetDashboardInput,
  IsLoading,
  QueryOptions,
  QueryStatus,
} from 'v2/core/repository/edge-server/types';

const useGetDashboard = (
  input: GetDashboardInput,
  options?: QueryOptions & {
    onCompleted?: (data: GetDashboardByTokenQuery) => void;
  },
): EdgeResponse<Dashboard> => {
  const { data, error, loading, called, networkStatus } =
    useGetDashboardByTokenQuery({
      variables: {
        ...input,
      },
      // i.e. notify on refetch
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
      skip: options?.Skip,
      pollInterval: options?.PollInterval,
      onCompleted: options?.onCompleted,
    });

  const dashboard = useMemo(
    () =>
      data?.getDashboardByToken && input.sourceDetailsMap
        ? mapFromEdgeDashboard(
            data.getDashboardByToken,
            input.sourceDetailsMap,
            input.timezone,
          )
        : undefined,
    [data?.getDashboardByToken, input.sourceDetailsMap, input.timezone],
  );
  const status = apolloStatusToQueryStatus(
    loading,
    called,
    options?.Skip ?? false,
    error,
    networkStatus,
  );

  return {
    data: dashboard,
    status:
      // In case of polling, status is loading
      // suppress and let it load in background
      IsLoading(status) && dashboard ? QueryStatus.SUCCESS_COMPLETED : status,
    error,
  };
};

export default useGetDashboard;
